import type {
  SpectralDopplerValues,
  SpectralDopplerToolVariant,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import {
  reportsTime,
  timeIntervalMs,
} from '../tools/SpectralDoppler/calibrationMath';

export interface AnnotationValueData {
  cachedStats?: Record<
    string,
    { length?: number; area?: number; angle?: number }
  >;
  simpsonStats?: { volume?: number; volumeUnit?: string };
  spectralValues?: SpectralDopplerValues;
  spectralToolVariant?: SpectralDopplerToolVariant;
}

/** Local copy of the unit normalization (lowercase, trim, ² → 2). Importing
 *  measurementToolMapping would drag the real Cornerstone tool classes into
 *  component test environments. */
function normalizeUnit(unit: string | null | undefined): string | null {
  if (!unit) return null;
  return unit.toLowerCase().trim().replace(/²/g, '2');
}

/** Primary display value for a spectral-Doppler annotation: peak velocity for
 *  probe/segment, VTI for the trace, time for Time (and Slope on a time row) —
 *  in the canonical's unit.
 *  Abbreviation renders separately. */
function formatDopplerPrimaryValue(
  variant: SpectralDopplerToolVariant | undefined,
  values: SpectralDopplerValues | undefined,
  precision: number,
  canonicalUnit?: string | null
): string {
  if (!variant || !values) return '';
  const normalized = normalizeUnit(canonicalUnit);
  if (reportsTime(variant, canonicalUnit)) {
    if (!Number.isFinite(values.deltaT_sec)) return '';
    return `${timeIntervalMs(values.deltaT_sec as number).toFixed(0)} ms`;
  }
  if (variant === 'vti') {
    if (!Number.isFinite(values.vti_cm)) return '';
    const unitLabel = normalized === 'cm' ? canonicalUnit! : 'cm';
    return `${(values.vti_cm as number).toFixed(precision)} ${unitLabel}`;
  }
  if (!Number.isFinite(values.peakV_cmS)) return '';
  // Peak velocity is reported as a magnitude (the derived value is signed by
  // baseline convention); match the AI/worksheet positive convention.
  const peakCmS = Math.abs(values.peakV_cmS as number);
  // Render in the canonical's unit — the precision is defined against it.
  // e' arrives as cm/s with precision 1; forcing m/s displayed "0.0 m/s".
  if (normalized === 'cm/s') {
    return `${peakCmS.toFixed(precision)} ${canonicalUnit!}`;
  }
  return `${(peakCmS / 100).toFixed(precision)} m/s`;
}

/** Precision when the annotation has no canonical measurement (or the
 *  canonical carries none). */
const DEFAULT_PRECISION = 2;

/** "4.2 cm" — an annotation's value in its canonical's unit and precision. */
export function formatAnnotationValue(
  data: AnnotationValueData | undefined,
  canonical?: { precision?: number | null; unit?: string | null }
): string {
  const precision = canonical?.precision ?? DEFAULT_PRECISION;
  const canonicalUnit = canonical?.unit ?? null;
  const normalizedUnit = normalizeUnit(canonicalUnit);

  const cachedStats = data?.cachedStats;
  const firstKey = cachedStats ? Object.keys(cachedStats)[0] : undefined;
  const stats = firstKey ? cachedStats?.[firstKey] : undefined;
  const simpson = data?.simpsonStats;
  const simpsonVol = simpson?.volume;
  const areaMm2 = stats?.area;
  const lengthMm = stats?.length;
  const angleDeg = stats?.angle;

  if (data?.spectralValues && data.spectralToolVariant) {
    return formatDopplerPrimaryValue(
      data.spectralToolVariant,
      data.spectralValues,
      precision,
      canonicalUnit
    );
  }
  if (typeof angleDeg === 'number' && Number.isFinite(angleDeg)) {
    return `${angleDeg.toFixed(precision)} ${canonicalUnit ?? '°'}`;
  }
  if (typeof simpsonVol === 'number' && Number.isFinite(simpsonVol)) {
    return `${simpsonVol.toFixed(precision)} ${canonicalUnit ?? simpson?.volumeUnit ?? 'mL'}`;
  }
  if (typeof areaMm2 === 'number' && Number.isFinite(areaMm2)) {
    if (normalizedUnit === 'mm2') {
      return `${areaMm2.toFixed(precision)} ${canonicalUnit}`;
    }
    const unitLabel = normalizedUnit === 'cm2' ? canonicalUnit! : 'cm²';
    return `${(areaMm2 / 100).toFixed(precision)} ${unitLabel}`;
  }
  if (typeof lengthMm === 'number' && Number.isFinite(lengthMm)) {
    if (normalizedUnit === 'mm') {
      return `${lengthMm.toFixed(precision)} ${canonicalUnit}`;
    }
    const unitLabel = normalizedUnit === 'cm' ? canonicalUnit! : 'cm';
    return `${(lengthMm / 10).toFixed(precision)} ${unitLabel}`;
  }
  return '';
}
